import { useState, useEffect, useCallback } from 'react';
import { 
  Shield, CheckCircle, XCircle, Clock, AlertTriangle,
  Building2, MapPin, Mail, Phone, FileText, User,
  RefreshCw, ChevronRight, Eye, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLAIM_STATUS_CONFIG = {
  pending: { 
    label: 'In Attesa', 
    color: 'bg-amber-100 text-amber-700', 
    icon: Clock 
  },
  pending_verification: { 
    label: 'Verifica Pendente', 
    color: 'bg-blue-100 text-blue-700', 
    icon: Shield 
  },
  approved: { 
    label: 'Approvato', 
    color: 'bg-green-100 text-green-700', 
    icon: CheckCircle 
  },
  rejected: { 
    label: 'Rifiutato', 
    color: 'bg-red-100 text-red-700', 
    icon: XCircle 
  },
  expired: { 
    label: 'Scaduto', 
    color: 'bg-gray-100 text-gray-700', 
    icon: AlertTriangle 
  },
};

const VERIFICATION_METHOD_LABELS = {
  phone: 'Telefono',
  email: 'Email',
  documents: 'Documenti',
};

export function ClaimsPanel() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/claims/pending`);
      if (!res.ok) throw new Error('Errore caricamento claims');
      const data = await res.json();
      setClaims(data.claims || []);
    } catch (err) {
      console.error('Failed to load claims:', err);
      toast.error('Errore caricamento richieste di proprietà');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const approveClaim = async (claimId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/admin/claims/${claimId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: adminNotes || null })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore approvazione');
      }

      toast.success('Claim approvato con successo');
      setSelectedClaim(null);
      setAdminNotes('');
      await loadClaims();
    } catch (err) {
      console.error('Approve failed:', err);
      toast.error(err.message || 'Errore durante l\'approvazione');
    } finally {
      setProcessing(false);
    }
  };

  const rejectClaim = async (claimId) => {
    if (!adminNotes.trim()) {
      toast.error('Inserisci una motivazione per il rifiuto');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/admin/claims/${claimId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_notes: adminNotes })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore rifiuto');
      }

      toast.success('Claim rifiutato');
      setSelectedClaim(null);
      setAdminNotes('');
      await loadClaims();
    } catch (err) {
      console.error('Reject failed:', err);
      toast.error(err.message || 'Errore durante il rifiuto');
    } finally {
      setProcessing(false);
    }
  };

  const pendingClaims = claims.filter(c => 
    c.status === 'pending' || c.status === 'pending_verification'
  );

  return (
    <div className="space-y-6" data-testid="claims-panel">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{pendingClaims.length}</p>
                <p className="text-xs text-gray-500">In Attesa</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {claims.filter(c => c.verification_method === 'documents').length}
                </p>
                <p className="text-xs text-gray-500">Con Documenti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {claims.filter(c => c.status === 'approved').length}
                </p>
                <p className="text-xs text-gray-500">Approvati</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Building2 className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{claims.length}</p>
                <p className="text-xs text-gray-500">Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claims List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Richieste di Proprietà
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadClaims}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto mb-3" />
              <p className="text-gray-500">Caricamento claims...</p>
            </div>
          ) : claims.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessuna richiesta di proprietà</p>
              <p className="text-sm text-gray-400 mt-1">
                Le richieste appariranno qui quando i fornitori cercheranno di reclamare un'attività
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {claims.map((claim) => {
                const statusConfig = CLAIM_STATUS_CONFIG[claim.status] || CLAIM_STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const isPending = claim.status === 'pending' || claim.status === 'pending_verification';
                
                return (
                  <div
                    key={claim.id}
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      isPending 
                        ? 'bg-amber-50 border-amber-200 hover:border-amber-300' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedClaim(claim)}
                    data-testid={`claim-card-${claim.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Provider Info */}
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-umbria-charcoal">
                            {claim.provider_info?.name || 'Provider sconosciuto'}
                          </h4>
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        
                        {/* Claim Details */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {claim.provider_info?.comune || 'N/A'}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {claim.claimed_by_email || claim.user_id?.slice(0, 8)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            {VERIFICATION_METHOD_LABELS[claim.verification_method] || claim.verification_method}
                          </span>
                        </div>
                        
                        {/* Date */}
                        <p className="text-xs text-gray-400 mt-2">
                          Richiesto: {new Date(claim.created_at).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { setSelectedClaim(null); setAdminNotes(''); }}
          />
          
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Dettaglio Claim</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => { setSelectedClaim(null); setAdminNotes(''); }}
                >
                  Chiudi
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Provider Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-umbria-charcoal mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Attività Reclamata
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nome</label>
                    <p>{selectedClaim.provider_info?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Tipo</label>
                    <p>{selectedClaim.provider_info?.type || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Comune</label>
                    <p>{selectedClaim.provider_info?.comune || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Fonte</label>
                    <Badge variant="secondary">{selectedClaim.provider_info?.source || 'google_places'}</Badge>
                  </div>
                </div>
              </div>

              {/* Claimant Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-umbria-charcoal mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Richiedente
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs font-medium text-gray-500">User ID</label>
                    <p className="font-mono text-xs">{selectedClaim.user_id}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Email</label>
                    <p>{selectedClaim.claimed_by_email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Metodo Verifica</label>
                    <Badge>{VERIFICATION_METHOD_LABELS[selectedClaim.verification_method] || selectedClaim.verification_method}</Badge>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Stato</label>
                    <Badge className={CLAIM_STATUS_CONFIG[selectedClaim.status]?.color}>
                      {CLAIM_STATUS_CONFIG[selectedClaim.status]?.label || selectedClaim.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Documents (if any) */}
              {selectedClaim.verification_method === 'documents' && selectedClaim.documents?.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h3 className="font-medium text-umbria-charcoal mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documenti Allegati
                  </h3>
                  <div className="space-y-2">
                    {selectedClaim.documents.map((doc, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <span className="text-sm">{doc.name || `Documento ${idx + 1}`}</span>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-umbria-olive hover:underline text-sm flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Visualizza
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              {(selectedClaim.status === 'pending' || selectedClaim.status === 'pending_verification') && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    <MessageSquare className="h-4 w-4 inline mr-1" />
                    Note Admin
                  </label>
                  <Textarea
                    placeholder="Aggiungi note (obbligatorie per rifiuto)..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    data-testid="claim-admin-notes"
                  />
                </div>
              )}

              {/* Actions */}
              {(selectedClaim.status === 'pending' || selectedClaim.status === 'pending_verification') && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => approveClaim(selectedClaim.id)}
                    disabled={processing}
                    className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
                    data-testid="approve-claim-btn"
                  >
                    {processing ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Approva Claim
                  </Button>
                  <Button
                    onClick={() => rejectClaim(selectedClaim.id)}
                    disabled={processing}
                    variant="destructive"
                    className="flex-1 gap-2"
                    data-testid="reject-claim-btn"
                  >
                    {processing ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    Rifiuta
                  </Button>
                </div>
              )}

              {/* Already Processed */}
              {selectedClaim.status === 'approved' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Questo claim è già stato approvato</span>
                  </div>
                  {selectedClaim.approved_at && (
                    <p className="text-sm text-green-600 mt-1">
                      Approvato il {new Date(selectedClaim.approved_at).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              )}

              {selectedClaim.status === 'rejected' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="h-5 w-5" />
                    <span className="font-medium">Questo claim è stato rifiutato</span>
                  </div>
                  {selectedClaim.admin_notes && (
                    <p className="text-sm text-red-600 mt-2">
                      Motivazione: {selectedClaim.admin_notes}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
